import { createComponent } from "@front/utils/alpine";

import Konva from "konva";
import { chunk } from "lodash-es";
import { nextTick } from "@front/utils";

const {
    Stage,
    Shape,
    TextPath,
    Layer,
} = Konva;

const CFontFamilyMap = [
    ["Open Sans Light", "normal"],
    ["League Script", "normal"],
    ["Playfair Display", "normal"],
] as const;

const STAGE_DEFAULT_SIZE = 750;

type TCanvas = {
    stage?: Konva.Stage;
    layer?: Konva.Layer;
    text?: Konva.TextPath;
};

type TBezierPoints = 'start' | 'control1' | 'control2' | 'end';

type TBezierAttrs = {
    attrs: {
        x: number;
        y: number;
    };
};

type TBezier = {
    [key in TBezierPoints]: TBezierAttrs;
};

function toggleBodyOverflow (force: boolean) {
    document.body.classList.toggle('md:overflow-y-hidden', force);
}

function toPoint (stage: Konva.Stage, x: number, y: number) {
    return new Shape({ x, y });
}

function getSVG (bezier: TBezier) {
    const [M1, M2, ...C ] = [
        bezier.start.attrs.x,
        bezier.start.attrs.y,
        bezier.control1.attrs.x,
        bezier.control1.attrs.y,
        bezier.control2.attrs.x,
        bezier.control2.attrs.y,
        bezier.end.attrs.x,
        bezier.end.attrs.y,
    ];

    return `M ${[M1, M2].join(',')} C ${C.join(' ')}`;
}

function getFont (fonts: { [key: number]: string }, fontName: string) {
    const idx = Object.values(fonts).findIndex(f => f == fontName) as number;
    return CFontFamilyMap[idx];
}

function getFontLabel (fonts: { [key: number]: string }, fontName: string) {
    const idx = CFontFamilyMap.findIndex(f => f == getFont(fonts, fontName)) as number;
    return fonts[idx + 1];
}

function initCanvas (container: string | HTMLDivElement, rawCurve: string, fontSize: string, placeholder = 'Votre texte', fontFamily = 'Open Sans Light', fontStyle = 'normal') {
    const stage = new Stage({
        container,
        width: STAGE_DEFAULT_SIZE,
        height: STAGE_DEFAULT_SIZE,
    });

    const text = new TextPath({
        x: 0,
        y: 0,
        fill: 'black',
        fontSize: parseInt(fontSize, 10),
        data: 'M0,100 500.0.0',
        align: 'center',
        text: placeholder,
        fontStyle,
        fontFamily,
    });

    const layer = new Layer();

    const points = chunk(rawCurve.split(',').map(rc => parseInt(rc, 10)), 2).map(([x, y]) => toPoint(stage, x, y));
    const bezier = ['start', 'control1', 'control2', 'end'].reduce((obj, val, idx) => {
        Object.assign(obj, { [val]: points[idx] });
        return obj;
    }, {}) as TBezier;

    layer.add(text);
    stage.add(layer);
    text.data(getSVG(bezier));

    return {
        stage,
        layer,
        text,
    };
}

function fitCanvas (container: HTMLElement, canvas: TCanvas) {
    const scale = container.offsetWidth / STAGE_DEFAULT_SIZE;

    canvas.stage?.width(STAGE_DEFAULT_SIZE * scale);
    canvas.stage?.height(STAGE_DEFAULT_SIZE * scale);
    canvas.stage?.scale({ x: scale, y: scale });
    nextTick(() => {canvas.layer?.draw()});
}

export default createComponent(({ effect }) => ({
    isOpened: false,

    canvas: {} as TCanvas,
    textValue: '',
    fontValue: undefined as string | undefined,
    fontNames: {},

    displayPrint: {
        textValue: '',
        fontValue: '',
    },

    open () {
        this.isOpened = true;
    },

    close () {
        this.isOpened = false;
    },

    async remove (event: { detail: any }) {
        const { action, method } = this.$refs.form as HTMLFormElement;
        const id_customization = event.detail;
        const body = new URLSearchParams();
        Object.entries({ id_customization, delete: 'true' }).forEach(([k, v]) => body.append(k, v));

        const response = await fetch(action, { method, body });
        const data = await response.text();

        if (data != 'true') {
            return;
        }

        this.displayPrint = {
            textValue: '',
            fontValue: '',
        };
    },

    async submit () {
        const { action, method } = this.$refs.form as HTMLFormElement;
        const body = new URLSearchParams(new FormData(this.$refs.form) as any);
        const response = await fetch(action, { method, body });
        const data = await response.text();

        (document.querySelector('.js-product-customization-id') as HTMLInputElement).value = data;

        this.displayPrint = {
            textValue: this.textValue,
            fontValue: getFontLabel(this.fontNames, this.fontValue),
        };

        this.close();
    },

    setup ({
        rawCurve,
        fontSize,
        maxLength,
        textValue,
        textPlaceholder,
        fontValue,
        fontNames,
    }: {
        rawCurve: string,
        fontSize: string,
        maxLength: string,
        textValue: string,
        textPlaceholder: string,
        fontValue: string
        fontNames: object,
    }) {
        this.$watch('isOpened', toggleBodyOverflow);
        toggleBodyOverflow(this.isOpened);

        this.$watch('fontValue', () => {
            const [fontFamily, fontStyle] = getFont(this.fontNames, this.fontValue);
            this.canvas.text.fontFamily(fontFamily);
            this.canvas.text.fontStyle(fontStyle);
            nextTick(() => {this.canvas.layer.draw()});
        });

        this.$watch('textValue', () => {
            this.canvas.text.text(this.textValue);
            this.canvas.layer.draw();
        });

        this.$watch('displayPrint', () => {
            this.$dispatch('printmaking:update', this.displayPrint)
        });

        effect(() => {
            this.$refs.remainingChars.innerText = parseInt(maxLength, 10) - this.textValue.length;
        });

        this.fontNames = fontNames;
        this.fontValue = fontValue;
        this.textValue = textValue;

        const [fontFamily, fontStyle] = getFont(this.fontNames, this.fontValue);
        this.canvas = initCanvas(this.$refs.canvas, rawCurve, fontSize, textPlaceholder, fontFamily, fontStyle);

        window.addEventListener('resize', () => fitCanvas(this.$refs.placeholder, this.canvas));
        nextTick(() => fitCanvas(this.$refs.placeholder, this.canvas));
    },
}));
